import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import "./CalculadoraTecnica.css";

/* ============================================================
   CONVERSIONES
============================================================ */

const CONVERSIONES = {
  "w-btu": {
    nombre: "Watts → BTU/h",
    entrada: "Watts",
    salida: "BTU/h",
    calcular: (v) => v * 3.412141633,
  },

  "btu-w": {
    nombre: "BTU/h → Watts",
    entrada: "BTU/h",
    salida: "Watts",
    calcular: (v) => v / 3.412141633,
  },

  "kw-btu": {
    nombre: "kW → BTU/h",
    entrada: "kW",
    salida: "BTU/h",
    calcular: (v) => v * 3412.141633,
  },

  "btu-kw": {
    nombre: "BTU/h → kW",
    entrada: "BTU/h",
    salida: "kW",
    calcular: (v) => v / 3412.141633,
  },

  "w-kw": {
    nombre: "Watts → kW",
    entrada: "Watts",
    salida: "kW",
    calcular: (v) => v / 1000,
  },

  "kw-w": {
    nombre: "kW → Watts",
    entrada: "kW",
    salida: "Watts",
    calcular: (v) => v * 1000,
  },

  "hp-w": {
    nombre: "HP → Watts",
    entrada: "HP",
    salida: "Watts",
    calcular: (v) => v * 745.699872,
  },

  "w-hp": {
    nombre: "Watts → HP",
    entrada: "Watts",
    salida: "HP",
    calcular: (v) => v / 745.699872,
  },

  "hp-kw": {
    nombre: "HP → kW",
    entrada: "HP",
    salida: "kW",
    calcular: (v) => v * 0.745699872,
  },

  "kw-hp": {
    nombre: "kW → HP",
    entrada: "kW",
    salida: "HP",
    calcular: (v) => v / 0.745699872,
  },

  "btu-hp": {
    nombre: "BTU/h → HP",
    entrada: "BTU/h",
    salida: "HP",
    calcular: (v) => v / 2544.433577,
  },

  "hp-btu": {
    nombre: "HP → BTU/h",
    entrada: "HP",
    salida: "BTU/h",
    calcular: (v) => v * 2544.433577,
  },
};

/* ============================================================
   FRACCIONES COMUNES DE HP
============================================================ */

const FRACCIONES_HP = [
  { label: "1/8 HP", value: 1 / 8 },
  { label: "1/6 HP", value: 1 / 6 },
  { label: "1/5 HP", value: 1 / 5 },
  { label: "1/4 HP", value: 1 / 4 },
  { label: "1/3 HP", value: 1 / 3 },
  { label: "1/2 HP", value: 1 / 2 },
  { label: "2/3 HP", value: 2 / 3 },
  { label: "3/4 HP", value: 3 / 4 },
  { label: "1 HP", value: 1 },
  { label: "1 1/4 HP", value: 1.25 },
  { label: "1 1/2 HP", value: 1.5 },
  { label: "2 HP", value: 2 },
];

/* ============================================================
   FUNCIONES
============================================================ */

const formatNumber = (numero, maxDecimals = 2) => {
  if (!Number.isFinite(numero)) return "0";

  return new Intl.NumberFormat("es-EC", {
    maximumFractionDigits: maxDecimals,
    minimumFractionDigits: 0,
  }).format(numero);
};

const obtenerFraccionHPCercana = (hp) => {
  if (!Number.isFinite(hp) || hp <= 0) {
    return null;
  }

  let cercana = FRACCIONES_HP[0];
  let diferencia = Math.abs(hp - cercana.value);

  FRACCIONES_HP.forEach((item) => {
    const nuevaDiferencia = Math.abs(hp - item.value);

    if (nuevaDiferencia < diferencia) {
      cercana = item;
      diferencia = nuevaDiferencia;
    }
  });

  return cercana;
};

/* ============================================================
   COMPONENTE
============================================================ */

export default function CalculadoraTecnica() {
  const [tipo, setTipo] = useState("w-btu");
  const [valor, setValor] = useState("");
  const [resultado, setResultado] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const [hpReferencia, setHpReferencia] = useState(0.25);

  const conversion = CONVERSIONES[tipo];

  /* ==========================================================
     REFERENCIA HP
  ========================================================== */

  const referenciaHP = useMemo(() => {
    const hp = Number(hpReferencia);

    return {
      hp,
      watts: hp * 745.699872,
      kw: hp * 0.745699872,
      btu: hp * 2544.433577,
    };
  }, [hpReferencia]);

  /* ==========================================================
     CALCULAR
  ========================================================== */

  const calcular = (e) => {
    e?.preventDefault();

    const numero = Number(String(valor).replace(",", "."));

    if (!Number.isFinite(numero) || numero < 0) {
      setResultado(null);
      return;
    }

    setResultado(conversion.calcular(numero));
    setCopiado(false);
  };

  /* ==========================================================
     LIMPIAR
  ========================================================== */

  const limpiar = () => {
    setValor("");
    setResultado(null);
    setCopiado(false);
  };

  /* ==========================================================
     CAMBIAR CONVERSIÓN
  ========================================================== */

  const cambiarConversion = (nuevoTipo) => {
    setTipo(nuevoTipo);
    setValor("");
    setResultado(null);
    setCopiado(false);
  };

  /* ==========================================================
     FRACCIÓN DEL RESULTADO HP
  ========================================================== */

  const fraccionResultadoHP =
    resultado !== null && conversion.salida === "HP"
      ? obtenerFraccionHPCercana(resultado)
      : null;

  /* ==========================================================
     COPIAR RESULTADO
  ========================================================== */

  const copiarResultado = async () => {
    if (resultado === null) return;

    let texto = `${formatNumber(resultado, 4)} ${conversion.salida}`;

    if (conversion.salida === "HP") {
      const fraccion = obtenerFraccionHPCercana(resultado);

      if (fraccion) {
        texto += ` - Aproximadamente ${fraccion.label}`;
      }
    }

    try {
      await navigator.clipboard.writeText(texto);

      setCopiado(true);

      setTimeout(() => {
        setCopiado(false);
      }, 1800);
    } catch (error) {
      console.error("No se pudo copiar el resultado:", error);
    }
  };

  /* ==========================================================
     DIRECCIONES DE LA CONVERSIÓN
  ========================================================== */

  const obtenerDirecciones = () => {
    if (tipo === "w-btu" || tipo === "btu-w") {
      return {
        izquierda: "w-btu",
        derecha: "btu-w",
        textoIzquierda: "W → BTU/h",
        textoDerecha: "BTU/h → W",
      };
    }

    if (tipo === "kw-btu" || tipo === "btu-kw") {
      return {
        izquierda: "kw-btu",
        derecha: "btu-kw",
        textoIzquierda: "kW → BTU/h",
        textoDerecha: "BTU/h → kW",
      };
    }

    if (tipo === "hp-w" || tipo === "w-hp") {
      return {
        izquierda: "hp-w",
        derecha: "w-hp",
        textoIzquierda: "HP → W",
        textoDerecha: "W → HP",
      };
    }

    if (tipo === "hp-kw" || tipo === "kw-hp") {
      return {
        izquierda: "hp-kw",
        derecha: "kw-hp",
        textoIzquierda: "HP → kW",
        textoDerecha: "kW → HP",
      };
    }

    if (tipo === "btu-hp" || tipo === "hp-btu") {
      return {
        izquierda: "btu-hp",
        derecha: "hp-btu",
        textoIzquierda: "BTU/h → HP",
        textoDerecha: "HP → BTU/h",
      };
    }

    return {
      izquierda: "w-btu",
      derecha: "btu-w",
      textoIzquierda: "W → BTU/h",
      textoDerecha: "BTU/h → W",
    };
  };

  const direcciones = obtenerDirecciones();

  return (
    <div className="calculadora-page">
      <Navbar />

      <main>
        {/* =====================================================
            HERO
        ===================================================== */}

        <section className="calculadora-hero">
          <div className="calculadora-container">
            <div className="calculadora-hero-card">
              <div className="calculadora-hero-text">
                <span className="calculadora-kicker">
                  HERRAMIENTA TÉCNICA
                </span>

                <h1>Calculadora técnica</h1>

                <p>
                  Realiza conversiones rápidas de potencia y capacidad para
                  refrigeración, climatización y servicio técnico.
                </p>

                <div className="calculadora-hero-tags">
                  <span>Watts</span>
                  <span>BTU/h</span>
                  <span>HP</span>
                  <span>kW</span>
                </div>
              </div>

              <div className="calculadora-hero-icon">
                <i className="bi bi-calculator-fill"></i>
              </div>
            </div>
          </div>
        </section>

        <div className="calculadora-container calculadora-content">
          {/* ===================================================
              INTRO
          =================================================== */}

          <section className="calculadora-intro">
            <span className="calculadora-section-kicker">
              CONVERSIÓN DE UNIDADES
            </span>

            <h2>Selecciona el cálculo que necesitas</h2>

            <p>
              Ingresa un valor, selecciona el tipo de conversión y obtén el
              resultado de forma inmediata. Puedes utilizar punto o coma para
              valores decimales.
            </p>
          </section>

          {/* ===================================================
              TARJETAS DE CONVERSIÓN
          =================================================== */}

          <section className="calculadora-conversion-grid">
            {/* WATTS - BTU */}

            <button
              type="button"
              className={`calculadora-conversion-card ${
                tipo === "w-btu" || tipo === "btu-w" ? "active" : ""
              }`}
              onClick={() => cambiarConversion("w-btu")}
            >
              <div className="calculadora-conversion-icon">
                <i className="bi bi-lightning-charge"></i>
              </div>

              <div>
                <strong>Watts ↔ BTU/h</strong>
                <span>Potencia y capacidad térmica</span>
              </div>
            </button>

            {/* KW - BTU */}

            <button
              type="button"
              className={`calculadora-conversion-card ${
                tipo === "kw-btu" || tipo === "btu-kw" ? "active" : ""
              }`}
              onClick={() => cambiarConversion("kw-btu")}
            >
              <div className="calculadora-conversion-icon">
                <i className="bi bi-snow2"></i>
              </div>

              <div>
                <strong>kW ↔ BTU/h</strong>
                <span>Conversión térmica</span>
              </div>
            </button>

            {/* HP - WATTS */}

            <button
              type="button"
              className={`calculadora-conversion-card ${
                tipo === "hp-w" || tipo === "w-hp" ? "active" : ""
              }`}
              onClick={() => cambiarConversion("hp-w")}
            >
              <div className="calculadora-conversion-icon">
                <i className="bi bi-gear"></i>
              </div>

              <div>
                <strong>HP ↔ Watts</strong>
                <span>Potencia mecánica</span>
              </div>
            </button>

            {/* HP - KW */}

            <button
              type="button"
              className={`calculadora-conversion-card ${
                tipo === "hp-kw" || tipo === "kw-hp" ? "active" : ""
              }`}
              onClick={() => cambiarConversion("hp-kw")}
            >
              <div className="calculadora-conversion-icon">
                <i className="bi bi-speedometer2"></i>
              </div>

              <div>
                <strong>HP ↔ kW</strong>
                <span>Conversión de potencia</span>
              </div>
            </button>

            {/* BTU - HP */}

            <button
              type="button"
              className={`calculadora-conversion-card ${
                tipo === "btu-hp" || tipo === "hp-btu" ? "active" : ""
              }`}
              onClick={() => cambiarConversion("btu-hp")}
            >
              <div className="calculadora-conversion-icon">
                <i className="bi bi-snow"></i>
              </div>

              <div>
                <strong>BTU/h ↔ HP</strong>
                <span>Capacidad térmica y potencia</span>
              </div>
            </button>
          </section>

          {/* ===================================================
              CALCULADORA PRINCIPAL
          =================================================== */}

          <section className="calculadora-main-grid">
            <div className="calculadora-panel">
              <div className="calculadora-panel-header">
                <div className="calculadora-panel-header-icon">
                  <i className="bi bi-calculator"></i>
                </div>

                <div>
                  <span>CALCULADORA</span>
                  <h2>{conversion.nombre}</h2>
                </div>
              </div>

              <form onSubmit={calcular}>
                {/* DIRECCIÓN */}

                <div className="calculadora-direction">
                  <button
                    type="button"
                    className={
                      tipo === direcciones.izquierda ? "active" : ""
                    }
                    onClick={() =>
                      cambiarConversion(direcciones.izquierda)
                    }
                  >
                    {direcciones.textoIzquierda}
                  </button>

                  <button
                    type="button"
                    className={
                      tipo === direcciones.derecha ? "active" : ""
                    }
                    onClick={() =>
                      cambiarConversion(direcciones.derecha)
                    }
                  >
                    {direcciones.textoDerecha}
                  </button>
                </div>

                {/* INPUT */}

                <div className="calculadora-field">
                  <label htmlFor="valorConversion">
                    Valor en {conversion.entrada}
                  </label>

                  <div className="calculadora-input-wrapper">
                    <input
                      id="valorConversion"
                      type="text"
                      inputMode="decimal"
                      value={valor}
                      onChange={(e) => {
                        setValor(e.target.value);
                        setResultado(null);
                        setCopiado(false);
                      }}
                      placeholder={
                        conversion.entrada === "BTU/h"
                          ? "Ej.: 12000"
                          : conversion.entrada === "HP"
                          ? "Ej.: 0,25"
                          : "Ej.: 1000"
                      }
                      autoComplete="off"
                    />

                    <span>{conversion.entrada}</span>
                  </div>
                </div>

                {/* BOTONES */}

                <div className="calculadora-buttons">
                  <button
                    type="submit"
                    className="calculadora-btn-primary"
                  >
                    <i className="bi bi-calculator-fill"></i>
                    Calcular
                  </button>

                  <button
                    type="button"
                    className="calculadora-btn-secondary"
                    onClick={limpiar}
                  >
                    <i className="bi bi-arrow-counterclockwise"></i>
                    Limpiar
                  </button>
                </div>
              </form>

              {/* =================================================
                  RESULTADO
              ================================================= */}

              <div
                className={`calculadora-result ${
                  resultado !== null ? "has-result" : ""
                }`}
              >
                <div>
                  <span>RESULTADO</span>

                  {resultado !== null ? (
                    <>
                      <div className="calculadora-result-value">
                        <strong>{formatNumber(resultado, 4)}</strong>
                        <small>{conversion.salida}</small>
                      </div>

                      {fraccionResultadoHP && (
                        <div className="calculadora-hp-equivalent">
                          <i className="bi bi-arrow-right-short"></i>

                          <span>Fracción aproximada:</span>

                          <strong>
                            {fraccionResultadoHP.label}
                          </strong>
                        </div>
                      )}
                    </>
                  ) : (
                    <p>
                      Ingresa un valor para realizar la conversión.
                    </p>
                  )}
                </div>

                {resultado !== null && (
                  <button
                    type="button"
                    onClick={copiarResultado}
                    title="Copiar resultado"
                  >
                    <i
                      className={
                        copiado
                          ? "bi bi-check-lg"
                          : "bi bi-copy"
                      }
                    ></i>
                  </button>
                )}
              </div>

              {copiado && (
                <div className="calculadora-copy-message">
                  <i className="bi bi-check-circle-fill"></i>
                  Resultado copiado
                </div>
              )}
            </div>

            {/* ===================================================
                REFERENCIAS
            =================================================== */}

            <aside className="calculadora-reference-panel">
              <div className="calculadora-reference-header">
                <i className="bi bi-book"></i>

                <div>
                  <span>REFERENCIA RÁPIDA</span>
                  <h3>Equivalencias matemáticas</h3>
                </div>
              </div>

              <div className="calculadora-reference-list">
                <div>
                  <span>1 Watt</span>
                  <strong>3,412 BTU/h</strong>
                </div>

                <div>
                  <span>1 kW</span>
                  <strong>3.412,14 BTU/h</strong>
                </div>

                <div>
                  <span>1 HP</span>
                  <strong>745,70 W</strong>
                </div>

                <div>
                  <span>1 HP</span>
                  <strong>0,7457 kW</strong>
                </div>

                <div>
                  <span>1 kW</span>
                  <strong>1,341 HP</strong>
                </div>

                <div>
                  <span>1 HP</span>
                  <strong>2.544,43 BTU/h</strong>
                </div>
              </div>

              <div className="calculadora-reference-note">
                <i className="bi bi-info-circle-fill"></i>

                <p>
                  Las conversiones mostradas corresponden a relaciones
                  matemáticas de potencia. La capacidad real de un equipo
                  de refrigeración depende de sus características y
                  condiciones de operación.
                </p>
              </div>
            </aside>
          </section>

          {/* ===================================================
              FRACCIONES DE HP
          =================================================== */}

          <section className="calculadora-hp-section">
            <div className="calculadora-section-heading">
              <div>
                <span className="calculadora-section-kicker">
                  FRACCIONES DE HP
                </span>

                <h2>Referencia rápida de potencia</h2>

                <p>
                  Selecciona una potencia habitual para consultar su
                  equivalencia matemática aproximada en Watts, kilowatts
                  y BTU/h.
                </p>
              </div>

              <div className="calculadora-section-heading-icon">
                <i className="bi bi-gear-wide-connected"></i>
              </div>
            </div>

            <div className="calculadora-hp-buttons">
              {FRACCIONES_HP.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className={
                    hpReferencia === item.value ? "active" : ""
                  }
                  onClick={() => setHpReferencia(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="calculadora-hp-result">
              <div className="calculadora-hp-main">
                <span>POTENCIA SELECCIONADA</span>

                <strong>
                  {
                    FRACCIONES_HP.find(
                      (item) => item.value === hpReferencia
                    )?.label
                  }
                </strong>
              </div>

              <div className="calculadora-hp-value">
                <span>Watts</span>

                <strong>
                  {formatNumber(referenciaHP.watts, 2)} W
                </strong>
              </div>

              <div className="calculadora-hp-value">
                <span>Kilowatts</span>

                <strong>
                  {formatNumber(referenciaHP.kw, 4)} kW
                </strong>
              </div>

              <div className="calculadora-hp-value">
                <span>BTU/h</span>

                <strong>
                  {formatNumber(referenciaHP.btu, 2)} BTU/h
                </strong>
              </div>
            </div>
          </section>

          {/* ===================================================
              INFORMACIÓN
          =================================================== */}

          <section className="calculadora-info-section">
            <div className="calculadora-info-card">
              <i className="bi bi-lightning-charge-fill"></i>

              <div>
                <h3>Potencia eléctrica</h3>

                <p>
                  Watts y kilowatts son unidades utilizadas para expresar
                  potencia. 1 kW equivale a 1.000 W.
                </p>
              </div>
            </div>

            <div className="calculadora-info-card">
              <i className="bi bi-snow2"></i>

              <div>
                <h3>Capacidad térmica</h3>

                <p>
                  BTU/h se utiliza frecuentemente para expresar capacidad
                  térmica en sistemas de aire acondicionado y
                  refrigeración.
                </p>
              </div>
            </div>

            <div className="calculadora-info-card">
              <i className="bi bi-gear-fill"></i>

              <div>
                <h3>Horsepower (HP)</h3>

                <p>
                  HP es una unidad de potencia. No debe confundirse
                  directamente con la capacidad frigorífica comercial de
                  un compresor.
                </p>
              </div>
            </div>
          </section>

          {/* ===================================================
              AVISO
          =================================================== */}

          <section className="calculadora-warning">
            <div className="calculadora-warning-icon">
              <i className="bi bi-exclamation-triangle-fill"></i>
            </div>

            <div>
              <h3>Importante para trabajos de refrigeración</h3>

              <p>
                Esta calculadora funciona como herramienta de apoyo para
                conversiones de unidades. Para seleccionar compresores,
                motores, componentes eléctricos o equipos de
                refrigeración, deben consultarse las especificaciones
                técnicas del fabricante y las condiciones reales de
                operación.
              </p>
            </div>
          </section>

          {/* ===================================================
              CTA
          =================================================== */}

          <section className="calculadora-cta">
            <div>
              <span>G&J FRÍO INDUSTRIAL Y DOMÉSTICO</span>

              <h2>¿Necesitas repuestos o asesoría?</h2>

              <p>
                Consulta nuestro catálogo de productos o comunícate con
                nosotros para obtener más información.
              </p>
            </div>

            <div className="calculadora-cta-buttons">
              <Link
                to="/productos"
                className="calculadora-cta-primary"
              >
                <i className="bi bi-box-seam"></i>
                Ver productos
              </Link>

              <Link
                to="/contacto"
                className="calculadora-cta-secondary"
              >
                <i className="bi bi-chat-dots"></i>
                Contacto
              </Link>
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}